import { useState } from "react";
import { useJobTypeForms, type JobTypeForm } from "./useJobTypeForms";
import { apiErrorCode } from "@/lib/api-client";
import "./ManageFormsDialog.css";

interface ManageFormsDialogProps {
  jobTypeId: string;
  open: boolean;
  onClose: () => void;
}

/** Maps A5 / Task-5 409 error codes to operator-friendly messages. */
function errorMessage(code: string): string {
  if (code === "cannot_delete_default") return "Cannot delete the default form.";
  if (code === "form_has_fields")
    return "This form has fields — reassign or delete them first.";
  if (code === "duplicate_classification_key")
    return "That classification key is already in use.";
  if (code === "duplicate_name") return "That form name is already in use.";
  return "Could not update form.";
}

interface FormRowProps {
  form: JobTypeForm;
  isFirst: boolean;
  isLast: boolean;
  onDelete: (formId: string) => void;
  onSetDefault: (formId: string) => void;
  onEditKey: (formId: string, classificationKey: string) => void;
  onReorder: (formId: string, direction: "up" | "down") => void;
  rowError: string | null;
}

function FormRow({
  form,
  isFirst,
  isLast,
  onDelete,
  onSetDefault,
  onEditKey,
  onReorder,
  rowError,
}: FormRowProps) {
  const [keyValue, setKeyValue] = useState(form.classificationKey);

  return (
    <div className="manage-forms-dialog__row">
      <span className="manage-forms-dialog__row-name">
        {form.name}
        {form.isDefault && <span className="manage-forms-dialog__default-badge">★ default</span>}
      </span>

      <div className="manage-forms-dialog__row-key-edit">
        <input
          type="text"
          className="manage-forms-dialog__key-input"
          value={keyValue}
          onChange={(e) => setKeyValue(e.target.value)}
          aria-label={`Classification key for ${form.name}`}
        />
        <button
          type="button"
          className="manage-forms-dialog__key-save-btn"
          onClick={() => onEditKey(form.id, keyValue)}
          disabled={keyValue.trim() === "" || keyValue === form.classificationKey}
          aria-label={`Save key for ${form.name}`}
        >
          Save
        </button>
      </div>

      <div className="manage-forms-dialog__row-actions">
        <button
          type="button"
          className="manage-forms-dialog__reorder-btn"
          onClick={() => onReorder(form.id, "up")}
          disabled={isFirst}
          aria-label={`Move ${form.name} up`}
        >
          ↑
        </button>
        <button
          type="button"
          className="manage-forms-dialog__reorder-btn"
          onClick={() => onReorder(form.id, "down")}
          disabled={isLast}
          aria-label={`Move ${form.name} down`}
        >
          ↓
        </button>
        <button
          type="button"
          className="manage-forms-dialog__default-btn"
          onClick={() => onSetDefault(form.id)}
          disabled={form.isDefault}
          aria-label={`Set form ${form.name} as default`}
        >
          Set default
        </button>
        <button
          type="button"
          className="manage-forms-dialog__delete-btn"
          onClick={() => onDelete(form.id)}
          disabled={form.isDefault}
          aria-label={
            form.isDefault ? "Cannot delete default form" : `Delete form ${form.name}`
          }
        >
          Delete
        </button>
      </div>

      {rowError && (
        <p className="manage-forms-dialog__inline-error" role="alert">
          {rowError}
        </p>
      )}
    </div>
  );
}

/**
 * ManageFormsDialog — lists all forms for a job type with manage affordances:
 * set-default, inline classification-key edit, up/down reorder, and delete.
 * Surfaces 409 errors inline (cannot_delete_default, form_has_fields,
 * duplicate_classification_key, duplicate_name).
 */
export function ManageFormsDialog({ jobTypeId, open, onClose }: ManageFormsDialogProps) {
  const { forms, remove, setDefault, rename, reorder } = useJobTypeForms(jobTypeId);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  if (!open) return null;

  function clearError(formId: string) {
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[formId];
      return next;
    });
  }

  function setError(formId: string, err: unknown) {
    setRowErrors((prev) => ({ ...prev, [formId]: errorMessage(apiErrorCode(err)) }));
  }

  async function handleDelete(formId: string) {
    clearError(formId);
    try {
      // remove() self-refreshes the list internally — do NOT refetch again
      // (the "one refetch per delete" contract, A8 review).
      await remove(formId);
    } catch (err) {
      setError(formId, err);
    }
  }

  async function handleSetDefault(formId: string) {
    clearError(formId);
    try {
      await setDefault(formId);
    } catch (err) {
      setError(formId, err);
    }
  }

  async function handleEditKey(formId: string, classificationKey: string) {
    clearError(formId);
    try {
      await rename(formId, { classificationKey });
    } catch (err) {
      setError(formId, err);
    }
  }

  async function handleReorder(formId: string, direction: "up" | "down") {
    clearError(formId);
    try {
      await reorder(formId, direction);
    } catch (err) {
      setError(formId, err);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Manage forms"
      className="manage-forms-dialog"
    >
      <div className="manage-forms-dialog__overlay" onClick={onClose} />
      <div className="manage-forms-dialog__panel">
        <div className="manage-forms-dialog__header">
          <h2 className="manage-forms-dialog__title">Manage Forms</h2>
          <button
            type="button"
            className="manage-forms-dialog__close-btn"
            onClick={onClose}
            aria-label="Close manage forms dialog"
          >
            ✕
          </button>
        </div>
        <div className="manage-forms-dialog__body">
          {forms.length === 0 ? (
            <p className="manage-forms-dialog__empty">No forms configured.</p>
          ) : (
            forms.map((form, index) => (
              <FormRow
                key={form.id}
                form={form}
                isFirst={index === 0}
                isLast={index === forms.length - 1}
                onDelete={handleDelete}
                onSetDefault={handleSetDefault}
                onEditKey={handleEditKey}
                onReorder={handleReorder}
                rowError={rowErrors[form.id] ?? null}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
